import { Component, ComponentType } from "@/types";
import { BaseCommandHandler, CommandResult } from "../BaseCommandHandler";

interface Assignment {
  id: string;
  name: string;
  role?: string;
  avatar?: string;
  tasks: { title: string; priority: string; completed: boolean }[];
  status: 'available' | 'busy' | 'away';
}

/**
 * Command Handler for Assignment Widget components.
 *
 * Handles commands for:
 * - Adding/removing team members
 * - Layout switching (list/grid/compact)
 * - Grouping (by role/status/phase)
 * - Status changes
 * - Show/hide toggles (task count, status)
 */
export class AssignmentCommandHandler extends BaseCommandHandler {
  constructor() {
    super();
    this.initializePatterns();
  }

  private initializePatterns(): void {
    // 1. Add person: "add person Alice Johnson" or "add member Bob Smith"
    this.commandPatterns.set(
      /add (?:a )?(?:person|member|team member) (?:named |called )?["']?(.+?)["']?\s*$/i,
      (match, component) => {
        const name = match[1].trim();
        const id = String(Date.now());
        const existingAssignments = (component.properties?.assignments || []) as Assignment[];
        const newAssignment: Assignment = {
          id,
          name,
          role: '',
          avatar: '',
          tasks: [],
          status: 'available',
        };
        return { properties: { assignments: [...existingAssignments, newAssignment] } };
      }
    );

    // 2. Remove person: "remove person Alice" or "remove member Bob Smith"
    this.commandPatterns.set(
      /(?:remove|delete) (?:the )?(?:person|member|team member) (?:named |called )?["']?(.+?)["']?\s*$/i,
      (match, component) => {
        const name = match[1].trim().toLowerCase();
        const existingAssignments = (component.properties?.assignments || []) as Assignment[];
        const filtered = existingAssignments.filter(
          (a) => a.name.toLowerCase() !== name
        );
        if (filtered.length === existingAssignments.length) return null;
        return { properties: { assignments: filtered } };
      }
    );

    // 3. Set layout: "set layout list" or "switch to grid layout" or "use compact"
    this.commandPatterns.set(
      /(?:set |switch (?:to )?|change (?:to )?|use )?layout (?:to )?(list|grid|compact)/i,
      (match) => ({ properties: { layout: match[1].toLowerCase() } })
    );

    // 3b. Alternative: "switch to grid" or "use compact layout"
    this.commandPatterns.set(
      /(?:switch to|use) (list|grid|compact)(?: layout)?/i,
      (match) => ({ properties: { layout: match[1].toLowerCase() } })
    );

    // 4. Group by: "group by role" or "group by status" or "group by phase"
    this.commandPatterns.set(
      /group (?:by )?(role|status|phase)/i,
      (match) => ({ properties: { groupBy: match[1].toLowerCase() } })
    );

    // 4b. Remove grouping: "ungroup" or "remove grouping"
    this.commandPatterns.set(
      /(?:ungroup|remove group(?:ing)?|clear group(?:ing)?|no group(?:ing)?)/i,
      () => ({ properties: { groupBy: undefined } })
    );

    // 5. Set status: "set status Alice available" or "set Bob to busy"
    this.commandPatterns.set(
      /set (?:status (?:of )?)?["']?(.+?)["']? (?:to |status )?(available|busy|away)/i,
      (match, component) => {
        const personName = match[1].trim().toLowerCase();
        const status = match[2].toLowerCase() as 'available' | 'busy' | 'away';
        const existingAssignments = (component.properties?.assignments || []) as Assignment[];
        const updated = existingAssignments.map((a) => {
          if (a.name.toLowerCase().includes(personName)) {
            return { ...a, status };
          }
          return a;
        });
        return { properties: { assignments: updated } };
      }
    );

    // 6. Show/hide task count
    this.commandPatterns.set(
      /(show|hide|enable|disable) task ?count/i,
      (match) => {
        const show = /^(show|enable)$/i.test(match[1]);
        return { properties: { showTaskCount: show } };
      }
    );

    // 7. Show/hide status
    this.commandPatterns.set(
      /(show|hide|enable|disable) status/i,
      (match) => {
        const show = /^(show|enable)$/i.test(match[1]);
        return { properties: { showStatus: show } };
      }
    );

    // 8. Set role: "set role of Alice to Designer"
    this.commandPatterns.set(
      /set role (?:of )?["']?(.+?)["']? (?:to |as )["']?(.+?)["']?\s*$/i,
      (match, component) => {
        const personName = match[1].trim().toLowerCase();
        const role = match[2].trim();
        const existingAssignments = (component.properties?.assignments || []) as Assignment[];
        const updated = existingAssignments.map((a) => {
          if (a.name.toLowerCase().includes(personName)) {
            return { ...a, role };
          }
          return a;
        });
        return { properties: { assignments: updated } };
      }
    );
  }

  canHandle(command: string, component: Component): boolean {
    if (component.type !== ComponentType.ASSIGNMENT_WIDGET) {
      return false;
    }

    const keywords = [
      "person", "member", "team member",
      "layout", "list", "grid", "compact",
      "group by", "ungroup",
      "status", "available", "busy", "away",
      "task count", "show status", "hide status",
      "role",
    ];
    const lowerCommand = command.toLowerCase().trim();
    return keywords.some((keyword) => lowerCommand.includes(keyword));
  }

  async parseCommand(command: string, component: Component): Promise<CommandResult> {
    const changes = this.matchPatterns(command, component);

    if (changes) {
      const validation = this.validateChanges(changes, component);
      if (!validation.valid) {
        return {
          success: false,
          error: validation.error,
          suggestions: await this.getSuggestions(component),
        };
      }

      return {
        success: true,
        command: {
          type: this.determineCommandType(changes),
          changes,
          confidence: this.calculateConfidence(command, changes),
          description: this.generateDescription(changes, component),
        },
        suggestions: await this.getSuggestions(component),
      };
    }

    return {
      success: false,
      error: "Could not understand the assignment widget command",
      suggestions: await this.getSuggestions(component),
    };
  }

  async getExampleCommands(): Promise<string[]> {
    return [
      'add person Alice Johnson',
      'remove person Bob Smith',
      'set layout grid',
      'switch to compact',
      'group by role',
      'group by status',
      'ungroup',
      'set status Alice to busy',
      'show task count',
      'hide status',
      'set role of Alice to Designer',
    ];
  }

  async getSuggestions(component: Component): Promise<string[]> {
    const props = component.properties || {};
    const suggestions: string[] = [];

    suggestions.push('add person New Member');

    if (props.layout === 'list' || !props.layout) {
      suggestions.push('set layout grid');
      suggestions.push('set layout compact');
    } else if (props.layout === 'grid') {
      suggestions.push('set layout list');
      suggestions.push('set layout compact');
    } else {
      suggestions.push('set layout list');
      suggestions.push('set layout grid');
    }

    if (!props.groupBy) {
      suggestions.push('group by role');
      suggestions.push('group by status');
    } else {
      suggestions.push('ungroup');
    }

    if (props.showTaskCount === false) {
      suggestions.push('show task count');
    } else {
      suggestions.push('hide task count');
    }

    if (props.showStatus === false) {
      suggestions.push('show status');
    } else {
      suggestions.push('hide status');
    }

    return suggestions;
  }
}

export const assignmentCommandHandler = new AssignmentCommandHandler();
